import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../../../components/ui/Button';
import { ExclamationFilledIcon } from '../../pets/pages/ReportDetailIcons';

export default function ReportProcessingErrorOverlay({
    error,
    onClose,
    onChangePhotos,
    onRetry,
}) {
    const { t } = useTranslation();

    useEffect(() => {
        if (!error) return;
        const onKeyDown = (e) => {
            if (e.key === 'Escape') onClose?.();
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [error, onClose]);

    if (!error) return null;

    const isInvalidSpecies = error.reason === 'invalid_species';

    const title = isInvalidSpecies
        ? t('pets.error_invalid_species_title')
        : t('pets.error_processing_title');

    const message = isInvalidSpecies
        ? t('pets.error_invalid_species')
        : error.reason === 'timeout'
            ? t('pets.error_processing_timeout')
            : t('pets.error_processing_failed');

    return (
        <div
            className="absolute inset-0 z-50 flex items-center justify-center p-4 sm:p-6 bg-black/60 dark:bg-black/75 backdrop-blur-xs animate-fadeIn select-none"
            onClick={(e) => {
                if (e.target === e.currentTarget) onClose?.();
            }}
            role="alertdialog"
            aria-modal="true"
        >
            <div className="relative w-full max-w-sm rounded-2xl border border-border-light dark:border-border-dark bg-white dark:bg-card-dark p-5 sm:p-6 shadow-2xl flex flex-col items-center text-center animate-scaleIn">
                {/* Close Button Top-Right */}
                <button
                    type="button"
                    onClick={onClose}
                    className="absolute top-3 right-3 w-7 h-7 rounded-full flex items-center justify-center text-subtle-light dark:text-subtle-dark hover:text-foreground-light dark:hover:text-foreground-dark bg-gray-50 dark:bg-white/5 hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
                    aria-label={t('common.close')}
                >
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>

                {/* Red Circular Alert Badge */}
                <div className="w-12 h-12 rounded-full bg-red-100 dark:bg-red-950/60 text-danger-light dark:text-danger-dark flex items-center justify-center mb-3 shrink-0 shadow-sm">
                    <ExclamationFilledIcon className="w-6 h-6 text-danger-light dark:text-danger-dark" />
                </div>

                {/* Title */}
                <h3 className="text-sm sm:text-base font-bold text-foreground-light dark:text-foreground-dark leading-snug">
                    {title}
                </h3>

                {/* Description */}
                <p className="text-xs sm:text-sm text-subtle-light dark:text-subtle-dark mt-2 leading-relaxed">
                    {message}
                </p>

                {/* Action Buttons */}
                <div className="mt-4 flex flex-col gap-2 w-full">
                    {isInvalidSpecies ? (
                        <>
                            <Button
                                type="button"
                                variant="primary"
                                size="sm"
                                onClick={onChangePhotos}
                                className="w-full font-medium"
                            >
                                {t('pets.change_photos')}
                            </Button>
                            <Button
                                type="button"
                                variant="neutral"
                                size="sm"
                                onClick={onClose}
                                className="w-full font-medium"
                            >
                                {t('pets.error_overlay_close')}
                            </Button>
                        </>
                    ) : (
                        <>
                            <Button
                                type="button"
                                variant="primary"
                                size="sm"
                                onClick={onRetry}
                                className="w-full font-medium"
                            >
                                {t('common.retry')}
                            </Button>
                            <Button
                                type="button"
                                variant="neutral"
                                size="sm"
                                onClick={onClose}
                                className="w-full font-medium"
                            >
                                {t('pets.error_overlay_close')}
                            </Button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
